// Fully customizable Abacus Test.
//  • Flash mode  — numbers flash automatically (mental-math anzan), type the sum.
//  • Typing mode — written problems (+ − × ÷ / mixed), type each answer.
// Reports right/wrong per question and a saved summary at the end.
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  generateFlashSequence,
  generateArithmeticProblem,
  operationLabel,
  formatNumber,
} from "./mathUtils";

const SPEED_PRESETS = [
  { label: "Steady", ms: 1000 },
  { label: "Brisk", ms: 700 },
  { label: "Fast", ms: 450 },
  { label: "Lightning", ms: 250 },
];

const DEFAULT_CONFIG = {
  mode: "flash", // 'flash' | 'typing'
  questions: 10,
  // flash
  terms: 5,
  digits: 1,
  speedMs: 700,
  allowSub: false,
  // typing
  operation: "add",
  typingDigits: 2,
};

export default function AbacusTest({ rodCount, onComplete }) {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [phase, setPhase] = useState("config"); // config | running | done
  const [questions, setQuestions] = useState([]);
  const [index, setIndex] = useState(0);
  const [review, setReview] = useState([]);
  const [guess, setGuess] = useState("");
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [startedAt, setStartedAt] = useState(0);
  const [savedSummary, setSavedSummary] = useState(null);

  // flash playback
  const [flashValue, setFlashValue] = useState(null);
  const [flashKey, setFlashKey] = useState(0);
  const [flashDone, setFlashDone] = useState(false);
  const [feedback, setFeedback] = useState(null); // 'right' | 'wrong'

  const timers = useRef([]);
  const inputRef = useRef(null);

  const clearTimers = useCallback(() => {
    timers.current.forEach((t) => clearTimeout(t));
    timers.current = [];
  }, []);
  useEffect(() => () => clearTimers(), [clearTimers]);

  function set(key, value) {
    setConfig((c) => ({ ...c, [key]: value }));
  }

  function buildQuestions() {
    const list = [];
    for (let i = 0; i < config.questions; i++) {
      if (config.mode === "flash") {
        list.push({
          type: "flash",
          ...generateFlashSequence({
            terms: config.terms,
            digits: config.digits,
            allowSubtraction: config.allowSub,
            maxRods: rodCount,
          }),
        });
      } else {
        const p = generateArithmeticProblem({
          operation: config.operation,
          digits: config.typingDigits,
        });
        list.push({ type: "typing", ...p });
      }
    }
    return list;
  }

  function startTest() {
    const qs = buildQuestions();
    setQuestions(qs);
    setIndex(0);
    setReview([]);
    setGuess("");
    setStreak(0);
    setBestStreak(0);
    setFeedback(null);
    setSavedSummary(null);
    setStartedAt(Date.now());
    setPhase("running");
  }

  // Drive the flash animation whenever we land on a flash question.
  useEffect(() => {
    if (phase !== "running") return;
    const q = questions[index];
    if (!q) return;
    clearTimers();
    setFeedback(null);
    setGuess("");

    if (q.type === "typing") {
      setFlashDone(true);
      setTimeout(() => inputRef.current && inputRef.current.focus(), 30);
      return;
    }

    setFlashDone(false);
    setFlashValue(null);
    q.sequence.forEach((term, i) => {
      timers.current.push(
        setTimeout(() => {
          setFlashValue(term);
          setFlashKey((k) => k + 1);
        }, i * config.speedMs)
      );
      timers.current.push(
        setTimeout(() => setFlashValue(null), i * config.speedMs + config.speedMs * 0.72)
      );
    });
    timers.current.push(
      setTimeout(() => {
        setFlashValue(null);
        setFlashDone(true);
        setTimeout(() => inputRef.current && inputRef.current.focus(), 30);
      }, q.sequence.length * config.speedMs)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, index, questions]);

  function submitAnswer(e) {
    e.preventDefault();
    const q = questions[index];
    if (!q) return;
    const right = Number(guess) === q.answer;
    const nextStreak = right ? streak + 1 : 0;
    setStreak(nextStreak);
    setBestStreak((b) => Math.max(b, nextStreak));
    setFeedback(right ? "right" : "wrong");

    const entry = {
      prompt: q.type === "typing" ? q.text : q.sequence,
      type: q.type,
      answer: q.answer,
      given: guess === "" ? null : Number(guess),
      right,
    };
    const nextReview = [...review, entry];
    setReview(nextReview);

    timers.current.push(
      setTimeout(() => {
        if (index + 1 < questions.length) {
          setIndex(index + 1);
        } else {
          finish(nextReview, Math.max(bestStreak, nextStreak));
        }
      }, 850)
    );
  }

  function finish(finalReview, finalBestStreak) {
    clearTimers();
    const correct = finalReview.filter((r) => r.right).length;
    const label = config.mode === "flash"
      ? `Flash · ${config.terms}×${config.digits}-digit`
      : `${operationLabel(config.operation)} · ${config.typingDigits}-digit`;
    const result = {
      mode: config.mode,
      label,
      total: finalReview.length,
      correct,
      bestStreak: finalBestStreak,
      durationMs: Date.now() - startedAt,
      review: finalReview,
    };
    setSavedSummary(result);
    setPhase("done");
    if (onComplete) onComplete(result);
  }

  function quit() {
    clearTimers();
    setPhase("config");
  }

  // ── Config screen ────────────────────────────────────────────────────────
  if (phase === "config") {
    return (
      <div className="abx-panel abx-test-config">
        <h3 className="abx-panel-title">Build your test</h3>

        <div className="abx-mode-switch">
          <button type="button"
            className={`abx-mode-btn ${config.mode === "flash" ? "is-on" : ""}`}
            onClick={() => set("mode", "flash")}>
            <span className="abx-mode-emoji">⚡</span>
            <span className="abx-mode-name">Mental Math (automatic flash)</span>
            <span className="abx-mode-sub">Numbers flash — add them and type the total</span>
          </button>
          <button type="button"
            className={`abx-mode-btn ${config.mode === "typing" ? "is-on" : ""}`}
            onClick={() => set("mode", "typing")}>
            <span className="abx-mode-emoji">⌨️</span>
            <span className="abx-mode-name">Typing answers</span>
            <span className="abx-mode-sub">Solve written problems and type each answer</span>
          </button>
        </div>

        <div className="abx-field">
          <label>Number of questions: <b>{config.questions}</b></label>
          <input type="range" min="3" max="30" value={config.questions}
            onChange={(e) => set("questions", Number(e.target.value))} />
        </div>

        {config.mode === "flash" ? (
          <>
            <div className="abx-field">
              <label>Numbers per question: <b>{config.terms}</b></label>
              <input type="range" min="2" max="15" value={config.terms}
                onChange={(e) => set("terms", Number(e.target.value))} />
            </div>
            <div className="abx-field">
              <label>Digits per number: <b>{config.digits}</b></label>
              <input type="range" min="1" max={Math.max(1, rodCount - 1)} value={config.digits}
                onChange={(e) => set("digits", Number(e.target.value))} />
            </div>
            <div className="abx-field">
              <label>Flash speed</label>
              <div className="abx-chip-row">
                {SPEED_PRESETS.map((sp) => (
                  <button key={sp.ms} type="button"
                    className={`abx-chip ${config.speedMs === sp.ms ? "is-on" : ""}`}
                    onClick={() => set("speedMs", sp.ms)}>{sp.label}</button>
                ))}
              </div>
            </div>
            <label className="abx-switch">
              <input type="checkbox" checked={config.allowSub}
                onChange={(e) => set("allowSub", e.target.checked)} />
              <span>Include subtraction</span>
            </label>
          </>
        ) : (
          <>
            <div className="abx-field">
              <label>Operation</label>
              <div className="abx-chip-row">
                {["add", "subtract", "multiply", "divide", "mixed"].map((op) => (
                  <button key={op} type="button"
                    className={`abx-chip ${config.operation === op ? "is-on" : ""}`}
                    onClick={() => set("operation", op)}>{operationLabel(op)}</button>
                ))}
              </div>
            </div>
            <div className="abx-field">
              <label>Difficulty (digits): <b>{config.typingDigits}</b></label>
              <input type="range" min="1" max="4" value={config.typingDigits}
                onChange={(e) => set("typingDigits", Number(e.target.value))} />
            </div>
          </>
        )}

        <button type="button" className="abx-btn abx-btn--primary abx-btn--wide" onClick={startTest}>
          Start test →
        </button>
      </div>
    );
  }

  // ── Running screen ───────────────────────────────────────────────────────
  if (phase === "running") {
    const q = questions[index];
    const progress = ((index) / questions.length) * 100;
    return (
      <div className="abx-panel abx-test-run">
        <div className="abx-test-topbar">
          <span className="abx-test-count">Question {index + 1} / {questions.length}</span>
          <div className="abx-progress"><span style={{ width: `${progress}%` }} /></div>
          <button type="button" className="abx-btn abx-btn--ghost" onClick={quit}>Quit</button>
        </div>

        <div className="abx-flash-stage abx-test-stage">
          {q.type === "flash" && !flashDone && (
            <div key={flashKey} className={`abx-flash-num ${flashValue == null ? "is-blank" : flashValue < 0 ? "is-neg" : "is-pos"}`}>
              {flashValue == null ? "·" : `${flashValue < 0 ? "−" : "+"}${formatNumber(Math.abs(flashValue))}`}
            </div>
          )}

          {q.type === "typing" && !feedback && (
            <div className="abx-typing-problem">{q.text} =</div>
          )}

          {feedback && (
            <div className={`abx-feedback ${feedback}`}>
              <span className="abx-feedback-mark">{feedback === "right" ? "✓" : "✗"}</span>
              <span>{feedback === "right" ? "Correct!" : `Answer: ${formatNumber(q.answer)}`}</span>
            </div>
          )}

          {((q.type === "flash" && flashDone) || q.type === "typing") && !feedback && (
            <form className="abx-answer-form" onSubmit={submitAnswer}>
              <input ref={inputRef} type="number" inputMode="numeric" className="abx-answer-input"
                value={guess} onChange={(e) => setGuess(e.target.value)}
                placeholder={q.type === "flash" ? "Total" : "Answer"} />
              <button type="submit" className="abx-btn abx-btn--primary">Submit</button>
            </form>
          )}
        </div>

        <div className="abx-streak-inline">🔥 Streak <b>{streak}</b> · Best <b>{bestStreak}</b></div>
      </div>
    );
  }

  // ── Done screen ──────────────────────────────────────────────────────────
  const r = savedSummary;
  const pct = r && r.total ? Math.round((r.correct / r.total) * 100) : 0;
  return (
    <div className="abx-panel abx-test-done">
      <div className={`abx-scorecard ${pct >= 80 ? "great" : pct >= 50 ? "ok" : "low"}`}>
        <div className="abx-score-ring" style={{ "--pct": pct }}>
          <div className="abx-score-inner">
            <span className="abx-score-pct">{pct}%</span>
            <span className="abx-score-frac">{r.correct}/{r.total}</span>
          </div>
        </div>
        <div className="abx-score-meta">
          <h3>{pct >= 80 ? "Outstanding!" : pct >= 50 ? "Nice work!" : "Keep practising!"}</h3>
          <p>{r.label}</p>
          <p className="abx-score-stats">
            🔥 Best streak <b>{r.bestStreak}</b> · ⏱ {(r.durationMs / 1000).toFixed(0)}s
          </p>
        </div>
      </div>

      <div className="abx-review">
        {r.review.map((item, i) => (
          <div key={i} className={`abx-review-row ${item.right ? "right" : "wrong"}`}>
            <span className="abx-review-num">{i + 1}</span>
            <span className="abx-review-prompt">
              {item.type === "typing"
                ? `${item.prompt} = ${formatNumber(item.answer)}`
                : item.prompt.map((t, j) => (j === 0 ? formatNumber(t) : `${t < 0 ? " − " : " + "}${formatNumber(Math.abs(t))}`)).join("") + ` = ${formatNumber(item.answer)}`}
            </span>
            <span className="abx-review-given">
              {item.right ? "✓" : item.given == null ? "✗ (blank)" : `✗ ${formatNumber(item.given)}`}
            </span>
          </div>
        ))}
      </div>

      <div className="abx-done-actions">
        <button type="button" className="abx-btn abx-btn--primary" onClick={startTest}>Retry same test</button>
        <button type="button" className="abx-btn" onClick={quit}>Change settings</button>
      </div>
    </div>
  );
}
